import { useState, useEffect, useCallback } from "react";

export type ThemeMode = "dark" | "light";

/**
 * Per-portal theme mode hook. Each portal (banker, investor, admin) keeps its
 * own preference in localStorage so a banker who likes day mode does not force
 * an investor's premium dark UI into light, and vice-versa.
 *
 * The portal layout renders the returned `mode` onto a `data-${scope}-theme`
 * attribute on its root element, and the corresponding CSS overrides in
 * `index.css` re-map hardcoded dark utilities to light ones (and vice-versa).
 *
 * Caller is responsible for placing the attribute and a Sun/Moon toggle in the
 * UI. This hook only manages state + persistence.
 */
export function useThemeMode(scope: "banker" | "investor" | "admin" | "founder" | "mentor", defaultMode: ThemeMode = "dark") {
  const storageKey = `${scope}_theme`;
  const [mode, setMode] = useState<ThemeMode>(() => {
    if (typeof window === "undefined") return defaultMode;
    try {
      const saved = localStorage.getItem(storageKey);
      return (saved === "dark" || saved === "light") ? saved : defaultMode;
    } catch { return defaultMode; }
  });

  useEffect(() => {
    try { localStorage.setItem(storageKey, mode); } catch {}
  }, [storageKey, mode]);

  const toggle = useCallback(() => {
    setMode(m => (m === "dark" ? "light" : "dark"));
  }, []);

  return { mode, setMode, toggle };
}
